import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const Products = () => {
  const products = [
    { 
      name: 'Makhana (Fox Nuts)', 
      slug: 'makhana', 
      description: 'Premium quality fox nuts sourced from Bihar. Available in various grades and sizes for export.',
      image: 'https://images.pexels.com/photos/4022090/pexels-photo-4022090.jpeg'
    },
    { 
      name: 'Basmati Rice', 
      slug: 'basmati-rice', 
      description: 'Aromatic long-grain basmati rice. Aged variants available with superior grain elongation.',
      image: 'https://images.pexels.com/photos/2589457/pexels-photo-2589457.jpeg'
    },
    { 
      name: 'Non-Basmati Rice', 
      slug: 'non-basmati-rice', 
      description: 'High-quality non-basmati varieties including Sona Masoori, IR64, and parboiled rice.',
      image: 'https://images.pexels.com/photos/7363671/pexels-photo-7363671.jpeg'
    },
    { 
      name: 'Dehydrated Onion Powder', 
      slug: 'dehydrated-onion-powder', 
      description: 'Industrial food-grade dehydrated onion powder with controlled moisture and granulation.',
      image: 'https://images.pexels.com/photos/6157049/pexels-photo-6157049.jpeg'
    },
    { 
      name: 'Vegetables', 
      slug: 'vegetables', 
      description: 'Fresh export-grade vegetables with seasonal sourcing and cold chain support.',
      image: 'https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg'
    },
    { 
      name: 'Fresh Fruits', 
      slug: 'fresh-fruits', 
      description: 'Export-quality fresh fruits with proper grading, sorting, and temperature-controlled handling.',
      image: 'https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg'
    },
    { 
      name: 'Spices', 
      slug: 'spices', 
      description: 'Whole and powdered spices with lab testing options and purity parameters.',
      image: 'https://images.pexels.com/photos/4198894/pexels-photo-4198894.jpeg'
    },
    { 
      name: 'Pulses', 
      slug: 'pulses', 
      description: 'Cleaned and graded pulses for bulk supply with consistent quality standards.',
      image: 'https://images.pexels.com/photos/4750274/pexels-photo-4750274.jpeg'
    },
    { 
      name: 'Coffee Beans', 
      slug: 'coffee-beans', 
      description: 'Arabica and Robusta coffee beans with export-grade packaging options.',
      image: 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg'
    },
    { 
      name: 'Tea', 
      slug: 'tea', 
      description: 'CTC and leaf tea varieties for bulk and retail pack supply.',
      image: 'https://images.pexels.com/photos/1638280/pexels-photo-1638280.jpeg'
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-light tracking-tight text-[#0B1B32] mb-6" data-testid="products-title">
              Our Products
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Premium quality agricultural exports across diverse categories. Each product undergoes rigorous quality checks to meet international standards.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Link
                key={product.slug}
                to={`/products/${product.slug}`}
                data-testid={`product-card-${product.slug}`}
                className="group relative overflow-hidden bg-white border border-slate-100 hover:border-slate-300 hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-medium text-white">
                    {product.name}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">{product.description}</p>
                  <span className="inline-flex items-center gap-2 text-[#0B1B32] font-medium group-hover:gap-4 transition-all duration-300">
                    View Details <ArrowRight size={18} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0B1B32] text-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
            Need a Custom Quote?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Contact us with your specific requirements and quantities
          </p>
          <Link
            to="/contact"
            data-testid="contact-us-button"
            className="inline-block bg-white text-[#0B1B32] hover:bg-slate-100 rounded-none px-8 py-3 font-medium transition-all duration-300 text-sm tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;